import { useState, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useFolders } from '../hooks/useFolders';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Upload, FileText, CheckCircle2, AlertTriangle, Loader2, X, FolderInput } from 'lucide-react';
import { toast } from 'sonner';
import { resolveIcon, resolveColor } from '../components/folders/folderMeta';
import { uploadVideoDirect, UploadCancelledError, formatSpeed, formatEta, formatBytes } from '../utils/uploadVideoDirect';
import { Progress } from '../components/ui/progress';

const MAX_VIDEO_MB = 2048; // 2 GB cap (mirrors backend MAX_VIDEO_UPLOAD_SIZE_MB)

export default function UploadPage() {
  const navigate = useNavigate();
  const api = useApi();
  const { post, get } = api;
  const [searchParams] = useSearchParams();
  const folders = useFolders();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // 'uploading' | 'processing' | 'done' | 'error'
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [folderId, setFolderId] = useState(() => searchParams.get('folder_id') || '');
  // Rich transfer stats for the direct-to-Tigris video upload flow. Reset
  // whenever a new upload starts; em-dashes are rendered for null fields.
  const [videoStats, setVideoStats] = useState({
    percent: 0, loadedBytes: 0, totalBytes: 0, speedBps: 0, etaSeconds: null,
  });
  const videoAbortRef = useRef(null);

  // Flat list of folders for the picker, with depth-aware indentation
  const flatFolders = useMemo(() => {
    const out = [];
    const walk = (nodes, depth) => {
      for (const n of nodes) {
        out.push({ folder_id: n.folder_id, name: n.name, depth, icon: n.icon, color: n.color });
        if (n.children) walk(n.children, depth + 1);
      }
    };
    walk(folders.tree, 0);
    return out;
  }, [folders.tree]);

  const selectedFolder = flatFolders.find(f => f.folder_id === folderId);
  const SelectedIcon = selectedFolder ? resolveIcon(selectedFolder.icon) : null;
  const selectedColor = selectedFolder ? resolveColor(selectedFolder.color) : null;

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['pptx', 'pdf', 'mp4'].includes(ext)) {
      toast.error('Only PPTX, PDF, and MP4 files are supported');
      return;
    }
    const isVideo = ext === 'mp4';
    const limitMb = isVideo ? MAX_VIDEO_MB : 100;
    if (f.size > limitMb * 1024 * 1024) {
      toast.error(`File too large. Maximum ${limitMb} MB for ${isVideo ? 'video' : 'document'} uploads.`);
      return;
    }
    setFile(f); setStatus(null); setResult(null);
  };

  // Probe an MP4 client-side for duration + poster frame (best-effort).
  const probeVideo = async (videoFile) => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(videoFile);
      const v = document.createElement('video');
      v.preload = 'metadata'; v.muted = true; v.src = url;
      let done = false;
      const finish = (out) => { if (done) return; done = true; URL.revokeObjectURL(url); resolve(out); };
      const tid = setTimeout(() => finish({}), 8000);
      v.addEventListener('loadedmetadata', () => { v.currentTime = Math.min(1, (v.duration || 1) / 2); });
      v.addEventListener('seeked', () => {
        try {
          const canvas = document.createElement('canvas');
          const ratio = (v.videoHeight || 360) / (v.videoWidth || 640);
          canvas.width = Math.min(640, v.videoWidth || 640);
          canvas.height = Math.round(canvas.width * ratio);
          canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
          clearTimeout(tid);
          finish({ posterDataUrl: canvas.toDataURL('image/jpeg', 0.7), durationSeconds: v.duration || null });
        } catch { clearTimeout(tid); finish({ durationSeconds: v.duration || null }); }
      });
      v.addEventListener('error', () => { clearTimeout(tid); finish({}); });
    });
  };

  const handleUpload = async () => {
    if (!file) return;
    const isVideo = file.name.toLowerCase().endsWith('.mp4');
    setUploading(true); setStatus('uploading');

    if (isVideo) {
      // ── Video-only module flow (Phase 2) ──
      // Step 1: probe duration + poster client-side (best-effort, ≤8s)
      // Step 2: direct browser → Tigris via presigned PUT (no FastAPI proxy)
      // Step 3: POST /videos/modules/from-video with the resulting file_id
      const ctrl = new AbortController();
      videoAbortRef.current = ctrl;
      setVideoStats({ percent: 0, loadedBytes: 0, totalBytes: file.size, speedBps: 0, etaSeconds: null });
      try {
        const meta = await probeVideo(file);
        // Yield one macrotask so Chrome releases the file blob from the
        // metadata-probe video element before we hand it to the XHR PUT.
        await new Promise((r) => setTimeout(r, 0));
        const uploadRes = await uploadVideoDirect({
          file, api, signal: ctrl.signal, onStats: setVideoStats,
        });
        setStatus('processing');
        const title = file.name.replace(/\.mp4$/i, '').slice(0, 200);
        const mod = await post('/videos/modules/from-video', {
          file_id: uploadRes.file_id,
          title,
          folder_id: folderId || undefined,
          duration_seconds: meta.durationSeconds || undefined,
          poster_dataurl: meta.posterDataUrl || undefined,
        });
        setStatus('done');
        setResult({ module_id: mod.module_id, processing_status: 'completed' });
      } catch (e) {
        if (e instanceof UploadCancelledError || e?.cancelled) {
          setStatus(null);  // back to the file-info card so they can retry
          toast.info('Upload cancelled.');
        } else {
          setStatus('error');
          toast.error(e.message || e.response?.data?.detail || 'Video upload failed');
        }
      } finally {
        setUploading(false);
        videoAbortRef.current = null;
      }
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folderId) formData.append('folder_id', folderId);
      const res = await post('/modules/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setStatus('processing');
      // Poll for status
      const fileId = res.file_id;
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const s = await get(`/modules/upload/${fileId}/status`);
          if (s.processing_status === 'completed') {
            clearInterval(poll);
            setStatus('done');
            setResult(s);
          } else if (s.processing_status === 'failed') {
            clearInterval(poll);
            setStatus('error');
            setResult(s);
          }
        } catch (err) { console.error("Upload processing failed:", err); }
        if (attempts > 60) { clearInterval(poll); setStatus('error'); }
      }, 3000);
    } catch (e) {
      const isMaintenance = e.response?.status === 503 && e.response?.data?.maintenance;
      setStatus('error');
      setResult(isMaintenance ? {
        processing_error: e.response.data.detail || 'Slide uploads temporarily unavailable, back in a few minutes.',
        maintenance: true,
      } : null);
      if (isMaintenance) {
        toast.warning(e.response.data.detail || 'Slide uploads temporarily unavailable.');
      } else {
        toast.error(e.response?.data?.detail || 'Upload failed');
      }
    } finally { setUploading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 relative" data-testid="upload-page">
      <div aria-hidden className="page-blob bg-[var(--lumyn-content)]" />
      <div className="relative z-10 space-y-5">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] text-[11px] font-semibold uppercase tracking-[0.14em]">
            <Upload className="w-3.5 h-3.5" />{'New module'}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{'Upload presentation'}</h1>
          <p className="text-sm text-muted-foreground">
            {'Drop a PDF, PPTX, or MP4 — Lumyn auto-detects sections (or creates a single-video module for MP4) and gets you to a draft in ~60 seconds.'}
          </p>
        </div>

      {/* Folder destination picker */}
      <Card>
        <CardContent className="pt-6 space-y-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <FolderInput className="w-4 h-4" />{'Destination folder'}
          </label>
          <Select value={folderId || 'root'} onValueChange={(v) => setFolderId(v === 'root' ? '' : v)}>
            <SelectTrigger data-testid="upload-folder-select" disabled={!!status}>
              <SelectValue>
                {selectedFolder ? (
                  <span className="flex items-center gap-2">
                    <SelectedIcon className="w-4 h-4" style={{ color: selectedColor }} />
                    {selectedFolder.name}
                  </span>
                ) : (
                  <span className="text-muted-foreground">Root (Unfiled)</span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="root">Root (Unfiled)</SelectItem>
              {flatFolders.map(f => {
                const Icon = resolveIcon(f.icon);
                const color = resolveColor(f.color);
                return (
                  <SelectItem key={f.folder_id} value={f.folder_id}>
                    <span className="flex items-center gap-2" style={{ paddingLeft: f.depth * 12 }}>
                      <Icon className="w-4 h-4" style={{ color }} />
                      {f.name}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            The processed module will land in this folder. You can move it later from the Content Editor.
          </p>
        </CardContent>
      </Card>

      {/* Drop zone */}
      <Card>
        <CardContent className="pt-6">
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/5' : 'border-border hover:border-muted-foreground/50'}`}
            data-testid="drop-zone"
          >
            <input ref={fileInputRef} type="file" accept=".pptx,.pdf,.mp4" onChange={e => handleFile(e.target.files?.[0])} className="hidden" />
            <Upload className="w-12 h-12 mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-lg font-medium">Drop your file here</p>
            <p className="text-sm text-muted-foreground mt-1">or click to browse</p>
            <p className="text-xs text-muted-foreground mt-3">Supports: PPTX, PDF (max 100 MB) &middot; MP4 (max {(MAX_VIDEO_MB / 1024).toFixed(0)} GB)</p>
          </div>
        </CardContent>
      </Card>

      {/* File info */}
      {file && !status && (
        <Card>
          <CardContent className="pt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-[var(--lumyn-content)]" />
              <div>
                <p className="text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setFile(null)}><X className="w-4 h-4" /></Button>
              <Button onClick={handleUpload} disabled={uploading} data-testid="upload-btn">{uploading ? 'Uploading...' : 'Upload & Process'}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status */}
      {status && (
        <Card>
          <CardContent className="pt-6 text-center space-y-4">
            {status === 'uploading' && (
              <>
                <Loader2 className="w-10 h-10 mx-auto text-[var(--lumyn-content)] animate-spin" />
                <p className="font-medium">Uploading…</p>
                {/* Rich stats only render for the MP4 path (totalBytes > 0).
                    The PPTX/PDF path doesn't use uploadVideoDirect, so videoStats
                    stays zeroed and this block is skipped — keeps the legacy
                    flow's UI untouched. */}
                {videoStats.totalBytes > 0 && (
                  <div className="space-y-2 text-left max-w-sm mx-auto" data-testid="upload-progress-card">
                    <Progress value={videoStats.percent} data-testid="upload-progress" />
                    <div className="flex items-center justify-between gap-3 text-xs tabular-nums text-muted-foreground">
                      <span data-testid="upload-bytes">
                        {formatBytes(videoStats.loadedBytes)} / {formatBytes(videoStats.totalBytes)}
                      </span>
                      <span data-testid="upload-percent">{videoStats.percent}%</span>
                    </div>
                    <div className="flex items-center justify-between gap-3 text-xs tabular-nums text-muted-foreground">
                      <span data-testid="upload-speed">{formatSpeed(videoStats.speedBps)}</span>
                      <span data-testid="upload-eta">ETA {formatEta(videoStats.etaSeconds)}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => videoAbortRef.current?.abort()}
                      data-testid="video-upload-cancel"
                    >
                      Cancel upload
                    </Button>
                  </div>
                )}
              </>
            )}
            {status === 'processing' && (
              <><Loader2 className="w-10 h-10 mx-auto text-[var(--lumyn-content)] animate-spin" /><p className="font-medium">Processing... Extracting slides</p><p className="text-sm text-muted-foreground">This may take a moment</p></>
            )}
            {status === 'done' && (
              <>
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                <p className="font-medium text-emerald-700">Complete!</p>
                {(result?.original_filename || file?.name) && (
                  <p className="text-sm text-muted-foreground" data-testid="upload-success-filename">
                    <span className="font-medium text-foreground">{result?.original_filename || file.name}</span> uploaded successfully
                  </p>
                )}
                {selectedFolder && (
                  <p className="text-sm text-muted-foreground">
                    Filed under <span className="font-medium" style={{ color: selectedColor }}>{selectedFolder.name}</span>
                  </p>
                )}
                {result?.module_id && (
                  <Button asChild data-testid="view-module-btn">
                    <a href={`/editor/modules/${result.module_id}`}>View Module</a>
                  </Button>
                )}
              </>
            )}
            {status === 'error' && (
              <>
                <AlertTriangle className={`w-10 h-10 mx-auto ${result?.maintenance ? 'text-amber-500' : 'text-red-500'}`} />
                <p className={`font-medium ${result?.maintenance ? 'text-amber-700' : 'text-red-700'}`}>
                  {result?.maintenance ? 'Uploads temporarily paused' : 'Processing failed'}
                </p>
                {result?.processing_error && <p className="text-sm text-muted-foreground" data-testid="upload-error-detail">{result.processing_error}</p>}
                <Button variant="outline" onClick={() => { setStatus(null); setFile(null); setResult(null); }}>
                  {result?.maintenance ? 'Try again later' : 'Try Again'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}
